<script lang="ts">
	// The menu filter on a restaurant's page (UC-31): one category, a word in
	// an item's name, or both. It works on the menu already on the page,
	// hiding and showing its rows, and asks the server for nothing. Drawn by
	// the worker with the page, then mounted over that markup; before then the
	// whole menu shows, which is the filter's own resting state.
	//
	// The page pins it at the top while the menu scrolls: the search, then
	// All categories and the first few in the owner's order, one tap away,
	// with the list of every category behind «القسم». The list is a single
	// Choice in a Sheet, as the Patterns board's pick from a list is: a tap
	// picks and closes. On a phone that sheet rises from the foot, as the item
	// sheet does, and the phone's Back closes it. The categories one tap away are pressed buttons, as
	// the report's periods are. How a word matches is filter.ts's.
	//
	// While the menu holds a photo, the row ends with the list or cards
	// switch from 640 px, after «القسم», so the diner changes the view from
	// anywhere in the menu (#1020). This island draws that copy itself,
	// because its mount replaces the bar's markup; under 640 px it hides, and
	// the copy in the «كل المطاعم» row shows instead. Only the quick
	// categories give way to it, by scrolling sideways.

	import { tick } from 'svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { restaurant } from './copy';
	import { ALL, fold, matches, offersEveryCategory, quickCategories } from './filter';
	import LayoutSwitch from './LayoutSwitch.svelte';
	import type { View } from './view.svelte';

	interface Category {
		name: string;
		/** How many items it holds in this shop's menu. */
		count: number;
	}

	interface Props {
		categories: Category[];
		/** The worker's first view, while the menu holds a photo; else no switch. */
		view?: View;
	}

	let { categories, view }: Props = $props();

	const WORD_MAX = 80;
	// The list's own values: Choice takes no '' for All categories, and a
	// category's name could be any word, so each option is named by its place.
	const EVERY = 'all';

	let category = $state(ALL);
	let word = $state('');
	let matched = $state(-1);
	let listing = $state(false);
	// The island's first part; the island itself is the page's pinned bar.
	let first = $state<HTMLElement>();

	const names = $derived(categories.map((c) => c.name));
	const quick = $derived(quickCategories(names, category));
	const listed = $derived(names.length > quick.length);
	const total = $derived(categories.reduce((sum, c) => sum + c.count, 0));
	const wanted = $derived(fold(word));
	const filtering = $derived(category !== ALL || wanted !== '');
	const options = $derived([
		{
			value: EVERY,
			label: restaurant.allCategories,
			sub: restaurant.itemCount(total),
			testId: 'catalogue-categories-all',
		},
		...categories.map((c, i) => ({
			value: `c${i}`,
			label: c.name,
			sub: restaurant.itemCount(c.count),
			testId: `catalogue-categories-${i}`,
		})),
	]);

	const optionOf = (name: string) => (name === ALL ? EVERY : `c${names.indexOf(name)}`);
	const nameOf = (value: string) =>
		value === EVERY ? ALL : (names[Number(value.slice(1))] ?? ALL);

	// The page's menu is outside this island, so the filter reaches it through
	// the marks the worker drew on it: data-menu-category on each category,
	// data-menu-item, with its folded name, on each item.
	$effect(() => {
		let shown = 0;
		for (const section of document.querySelectorAll<HTMLElement>('[data-menu-category]')) {
			const inCategory = category === ALL || section.dataset.menuCategory === category;
			let rows = 0;
			for (const item of section.querySelectorAll<HTMLElement>('[data-menu-item]')) {
				const keep = inCategory && matches(item.dataset.menuItem ?? '', wanted);
				item.hidden = !keep;
				if (keep) rows++;
			}
			section.hidden = rows === 0;
			shown += rows;
		}
		matched = shown;
	});

	// The picked category's button comes into view in its row, which may
	// have scrolled sideways. A diner who picks a category below the menu's
	// start is taken back to it, so the category starts under the bar rather
	// than out of sight.
	async function pick(name: string) {
		category = name;
		await tick();
		const pinned = first?.parentElement;
		pinned
			?.querySelector('.quick [aria-pressed="true"]')
			?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		const menu = document.querySelector<HTMLElement>('[data-menu]');
		if (!menu || !pinned) return;
		const under = pinned.getBoundingClientRect().bottom;
		const start = menu.getBoundingClientRect().top;
		if (start < under) window.scrollBy({ top: start - under });
	}

	function pickFromList(value: string | string[]) {
		listing = false;
		void pick(nameOf(String(value)));
	}

	function clear() {
		category = ALL;
		word = '';
	}
</script>

<div class="search" bind:this={first}>
	<Field
		testId="catalogue-word"
		ariaLabel={restaurant.word}
		type="search"
		value={word}
		placeholder={restaurant.word}
		maxLength={WORD_MAX}
		onInput={(v) => (word = v.slice(0, WORD_MAX))}
	/>
</div>

<div class="categories">
	<div class="quick" role="group" aria-label={restaurant.category}>
		<Button
			testId="catalogue-category-all"
			label={restaurant.allCategories}
			tone={category === ALL ? 'ink' : 'secondary'}
			size="sm"
			pressed={category === ALL}
			onPress={() => void pick(ALL)}
		/>
		{#each quick as name, i (name)}
			<Button
				testId="catalogue-category-{i}"
				label={name}
				tone={category === name ? 'ink' : 'secondary'}
				size="sm"
				pressed={category === name}
				onPress={() => void pick(name)}
			/>
		{/each}
	</div>
	{#if listed}
		<Button
			testId="catalogue-categories"
			label={restaurant.category}
			tone="secondary"
			size="sm"
			trailingIcon="expand"
			onPress={() => (listing = true)}
		/>
	{/if}
	{#if view}
		<div class="view" data-view-place="bar">
			<LayoutSwitch first={view} place="bar" />
		</div>
	{/if}
</div>

{#if filtering && matched === 0}
	<Card pad testId="catalogue-filter-none">
		<div class="none">
			<p class="copy">{restaurant.filterNone}</p>
			<div class="offers">
				<Button
					testId="catalogue-filter-none-clear"
					tone="text"
					size="sm"
					label={restaurant.clearFilter}
					onPress={clear}
				/>
				{#if offersEveryCategory(category, wanted)}
					<Button
						testId="catalogue-filter-none-all"
						tone="text"
						size="sm"
						label={restaurant.searchAll}
						onPress={() => void pick(ALL)}
					/>
				{/if}
			</div>
		</div>
	</Card>
{/if}

{#if filtering && matched >= 0}
	<div class="count">
		<span role="status" data-testid="catalogue-filter-count">
			{restaurant.itemCount(matched)} · {category === ALL ? restaurant.allCategories : category}
		</span>
		<Button
			testId="catalogue-clear-filter"
			tone="text"
			size="sm"
			label={restaurant.clearFilter}
			onPress={clear}
		/>
	</div>
{/if}

<Sheet
	open={listing}
	title={restaurant.categories}
	closeLabel={restaurant.close}
	onClose={() => (listing = false)}
	placement="auto"
	handle
	history
	testId="catalogue-categories-sheet"
	closeTestId="catalogue-categories-close"
	handleTestId="catalogue-categories-handle"
>
	<Choice
		{options}
		value={optionOf(category)}
		onChange={pickFromList}
		stack
		testId="catalogue-categories-list"
	/>
</Sheet>

<style>
	.search {
		display: flex;
		flex-direction: column;
	}

	.categories {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}

	/* The switch keeps its width, 16 px from «القسم». */
	.view {
		flex: none;
		margin-inline-start: var(--space-3);
	}

	/* Under 640 px: the negation of the query that shows the back copy's
	   hiding, so every width shows exactly one copy, fractions included. */
	@media not all and (min-width: 640px) {
		.view {
			display: none;
		}
	}

	/* One line: what does not fit scrolls sideways. */
	.quick {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		gap: var(--space-2);
		overflow-x: auto;
		scrollbar-width: none;
	}

	.count {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-5);
		font-size: var(--text-2xs);
		color: var(--text-body);
	}

	.none {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
	}

	.offers {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2) var(--space-6);
	}

	.copy {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}
</style>
